import { useAppSelector } from "hook/redux";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { linkProjectCreate } from "routes";
import { getEnvVariableValue } from "util/CommonUtil";
import { ENV_VAR } from "util/Constant";

const DOCS_BOT_ID = getEnvVariableValue(ENV_VAR.VITE_DOCS_BOT_ID);

// Helper function to wait for an element to appear in the DOM
const waitForElement = (selector: string): Promise<Element | null> => {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  });
};

// Helper function to load the widget script
const loadWidgetScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.async = true;
    script.src = "https://widget.docsbot.ai/chat.js";

    const firstScript = document.getElementsByTagName("script")[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    script.addEventListener("load", () => resolve());
    script.addEventListener("error", (e) => reject(e.message));
  });
};

// Helper function to initialize the widget
const initializeWidget = async (config: any): Promise<boolean> => {
  await loadWidgetScript();
  await Promise.all([
    window.DocsBotAI.mount(Object.assign({}, config)),
    waitForElement("#docsbotai-root"),
  ]);
  return true;
};

const DocsBotAIWidget = () => {
  const location = useLocation();
  const currentMessageLength = useAppSelector((state) => state.session.selectedConversation)
    .messages.length;

  useEffect(() => {
    if (!window.DocsBotAI) {
      window.DocsBotAI = window.DocsBotAI || {};

      window.DocsBotAI.init = initializeWidget;

      if (!window.DocsBotAI?.el) {
        window.DocsBotAI.init({ id: DOCS_BOT_ID })
          .then(() => {
            console.log("DocsBotAI initialized successfully");
          })
          .catch((err: any) => {
            console.error("DocsBotAI failed to initialize:", err);
          });
      }
    }

    return () => {
      try {
        window.DocsBotAI?.unmount?.();
      } catch (e) {
        console.error("Error while unmounting DocsBotAI:", e);
      }
    };
  }, []);

  useEffect(() => {
    const automaticPosition = () => {
      const widget: any = window.DocsBotAI?.el;
      if (widget) {
        const regex = /\/session\/[a-zA-Z0-9-]+/;
        if (regex.test(location.pathname) && currentMessageLength > 0) {
          widget?.classList?.add("should-go-up");
        } else {
          widget?.classList?.remove?.("should-go-up");
        }
      }
    };

    setTimeout(() => {
      automaticPosition();
    }, 500);
  }, [location, window.DocsBotAI, currentMessageLength]);

  return <div id="docsbotai-root" />;
};

export default DocsBotAIWidget;
